import type VueRouter from 'vue-router';
import type { NavigationGuard, Route, RouteConfig } from 'vue-router';
import type { LoadTiming } from '@cognitoforms/utils/load-timing';

type RouteLoadTimer = Pick<LoadTiming<never>, 'begin' | 'mark' | 'cancel'>;

/**
 * Times loads of a route when set as the route's `meta.loadTiming`; takes effect once `installRouteLoadTiming` is called.
 */
export type RouteLoadTiming = {
	/** The load timing for the route, which is marked `route-guards-done` once the global navigation guards have run. */
	timing: RouteLoadTimer;
	/** Determines whether a navigation to the route starts a new load, as opposed to changing state within a loaded page. */
	begins(to: Route, from: Route): boolean;
};

declare module 'vue-router/types/router' {
	interface RouteMeta {
		loadTiming?: RouteLoadTiming;
	}
}

function isInitialNavigation(from: Route): boolean {
	return from.path === '/' && from.matched.length === 0;
}

function collectTimers(routes: RouteConfig[] = [], timers = new Set<RouteLoadTimer>()): Set<RouteLoadTimer> {
	for (const route of routes) {
		if (route.meta?.loadTiming)
			timers.add(route.meta.loadTiming.timing);
		collectTimers(route.children, timers);
	}
	return timers;
}

/**
 * Times the loads of routes that set `meta.loadTiming`. A navigation that begins a load starts its timing before any
 * other global guard runs, `route-guards-done` is marked after the guards registered before this call, and the timing is
 * cancelled when the router navigates elsewhere, a navigation fails or the page is hidden. Call it after registering the
 * global `beforeEach` guards and before any `onError` handler.
 */
export function installRouteLoadTiming(router: VueRouter): void {
	const timers = collectTimers(router.options.routes);
	const cancelAll = () => timers.forEach(timer => timer.cancel());

	const beginLoad: NavigationGuard = (to, from, next) => {
		const loadTiming = to.meta?.loadTiming;
		if (loadTiming?.begins(to, from))
			loadTiming.timing.begin(isInitialNavigation(from) ? 'hard' : 'spa');
		next();
	};
	(router as unknown as { beforeHooks: NavigationGuard[] }).beforeHooks.unshift(beginLoad);

	router.beforeEach((to, from, next) => {
		to.meta?.loadTiming?.timing.mark('route-guards-done');
		next();
	});

	router.afterEach(to => {
		for (const timer of timers) {
			if (timer !== to.meta?.loadTiming?.timing)
				timer.cancel();
		}
	});

	router.onError(cancelAll);

	window.addEventListener('pagehide', cancelAll);
}
